import React from "react";

interface TemplateTextProps {
  /**
   * Template string with patterns like "{CVR-1-34580820|name}" that will be converted
   * Can include HTML markup which will be rendered
   */
  template: string;
  /**
   * Whether to convert matches to clickable links (defaults to true)
   */
  makeLinks?: boolean;
  /**
   * Optional callback when a link is clicked, receives the ID portion
   */
  onLinkClick?: (id: string) => void;
  /**
   * Optional class name for the links
   */
  linkClassName?: string;
}

/**
 * Component that parses template strings and optionally converts patterns to links
 * Pattern format: {ID|displayText} -> converts to either displayText or a link with displayText
 * Renders HTML content within the template string
 */
const TemplateText: React.FC<TemplateTextProps> = ({
  template,
  makeLinks = true,
  onLinkClick,
  linkClassName,
}) => {
  const parseTemplate = (text: string) => {
    // Regex to match the pattern {ID|displayText}
    const regex = /\{([^|]+)\|([^}]+)\}/g;
    let lastIndex = 0;
    const elements: React.ReactNode[] = [];

    // Find all matches
    let match;
    while ((match = regex.exec(text)) !== null) {
      // Add text before the match as HTML
      if (match.index > lastIndex) {
        const htmlContent = text.substring(lastIndex, match.index);
        elements.push(
          <span
            key={`html-${lastIndex}`}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />,
        );
      }

      // Extract id and display text
      const [fullMatch, displayText, id] = match;

      // Add the matched part, either as a link or plain text with HTML
      if (makeLinks) {
        elements.push(
          <a
            key={`${id}-${match.index}`}
            href="#"
            className={linkClassName}
            onClick={(e) => {
              e.preventDefault();
              onLinkClick?.(id);
            }}
            dangerouslySetInnerHTML={{ __html: displayText }}
          />,
        );
      } else {
        elements.push(
          <span
            key={`display-${match.index}`}
            dangerouslySetInnerHTML={{ __html: displayText }}
          />,
        );
      }

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text as HTML
    if (lastIndex < text.length) {
      const htmlContent = text.substring(lastIndex);
      elements.push(
        <span
          key={`html-end-${lastIndex}`}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />,
      );
    }

    return elements;
  };

  return <>{parseTemplate(template)}</>;
};

export default TemplateText;
